/**
 * Transform API notification data to NotificationCard format.
 * Branching follows getMessagesApp payload: actionType first, then injected ids
 * (targetType/targetId, commitment_id, invitation_id, dbId, offer_id).
 */
import { formatDateTimeCompact } from "./dateUtils";

type NotificationAction =
  | "accept"
  | "reject"
  | "deny"
  | "answer"
  | "clarify"
  | "issue";

/** Internal routing type for UI + useNotificationActions (not raw API strings). */
export type NotificationRoutingType =
  | "invitation"
  | "offer"
  | "badge-request"
  | "question"
  | "notify"
  | "nudge"
  | "unknown";

type NotificationTargetResolution =
  | {
      actionable: true;
      targetType: "commitment" | "invitation";
      targetId: string;
      actionKey: string | null;
    }
  | { actionable: false };

const normalizeType = (value: any) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .trim();

/**
 * Legacy `actionType: "commitment"` is used for multiple flows; disambiguate only
 * for that value (per backend note). Prefer explicit offer / badge-request when present.
 */
const commitmentRowToRoutingType = (message: string): "offer" | "badge-request" => {
  const m = message.toLowerCase();
  if (m.includes("requested a badge")) {
    return "badge-request";
  }
  return "offer";
};

/**
 * Primary discriminator: server `actionType`, then targetType. No message parsing except
 * for legacy `commitment` rows where the API uses one type for multiple flows.
 * Do not infer `question` from message body when `actionType` is NA — wait for payload.
 */
export const resolveNotificationActionType = (
  notification: any,
  targetType: "commitment" | "invitation" | null
): NotificationRoutingType => {
  const raw = notification?.actionType ?? "";
  const explicit = normalizeType(raw);

  if (!String(raw).trim() || explicit === "na" || explicit === "n-a") {
    if (targetType === "invitation") return "invitation";
    if (targetType === "commitment") return "offer";
    return "unknown";
  }

  if (explicit === "nminvitation" || explicit.includes("nminvitation")) {
    return "invitation";
  }
  if (explicit === "invitation") {
    return "invitation";
  }
  if (explicit === "offer") {
    return "offer";
  }
  if (explicit === "badge-request" || explicit.includes("badge-request")) {
    return "badge-request";
  }
  if (explicit.includes("badge") && explicit.includes("request")) {
    return "badge-request";
  }
  if (explicit === "question") {
    return "question";
  }
  if (explicit === "notify") {
    return "notify";
  }
  if (explicit === "commitment") {
    return commitmentRowToRoutingType(String(notification?.message || ""));
  }
  if (
    explicit.includes("nudge") ||
    explicit.includes("question-to-answer") ||
    explicit.includes("questiontoanswer") ||
    explicit.includes("action-required") ||
    explicit.includes("actionrequired")
  ) {
    return "nudge";
  }

  const message = String(notification?.message || "").toLowerCase();
  if (
    message.includes("question to answer") ||
    message.includes("questions to answer")
  ) {
    return "nudge";
  }

  if (targetType === "invitation") return "invitation";
  if (targetType === "commitment") return "offer";

  return "unknown";
};

export const resolveNotificationTarget = (
  notification: any
): NotificationTargetResolution => {
  const actionable = notification?.actionable === "yes";
  if (!actionable) {
    return { actionable: false };
  }

  const actionKey = notification?.actionKey
    ? String(notification.actionKey)
    : null;

  const targetType =
    notification?.targetType === "commitment" ||
    notification?.targetType === "invitation"
      ? notification.targetType
      : null;
  const targetId = notification?.targetId
    ? String(notification.targetId)
    : null;

  if (targetType && targetId) {
    return {
      actionable: true,
      targetType,
      targetId,
      actionKey,
    };
  }

  if (notification?.commitment_id) {
    return {
      actionable: true,
      targetType: "commitment",
      targetId: String(notification.commitment_id),
      actionKey,
    };
  }

  if (notification?.invitation_id) {
    return {
      actionable: true,
      targetType: "invitation",
      targetId: String(notification.invitation_id),
      actionKey,
    };
  }

  if (notification?.offer_id) {
    return {
      actionable: true,
      targetType: "commitment",
      targetId: String(notification.offer_id),
      actionKey,
    };
  }

  if (
    notification?.commitmentId ||
    notification?.commitment?._id ||
    notification?.commitment?.id
  ) {
    return {
      actionable: true,
      targetType: "commitment",
      targetId: String(
        notification?.commitmentId ||
          notification?.commitment?._id ||
          notification?.commitment?.id
      ),
      actionKey,
    };
  }

  if (
    notification?.invitationId ||
    notification?.invitation?._id ||
    notification?.invitation?.id
  ) {
    return {
      actionable: true,
      targetType: "invitation",
      targetId: String(
        notification?.invitationId ||
          notification?.invitation?._id ||
          notification?.invitation?.id
      ),
      actionKey,
    };
  }

  const at = normalizeType(notification?.actionType);
  const dbId = notification?.dbId != null ? String(notification.dbId) : null;

  if (dbId) {
    if (
      at === "invitation" ||
      at === "nminvitation" ||
      String(notification?.actionType || "").toLowerCase().includes("nminvitation")
    ) {
      return {
        actionable: true,
        targetType: "invitation",
        targetId: dbId,
        actionKey,
      };
    }
    if (
      at === "commitment" ||
      at === "offer" ||
      at === "badge-request" ||
      at === "question" ||
      at === "nudge" ||
      at.includes("nudge") ||
      at.includes("question") ||
      at === "notify" ||
      at.includes("action-required") ||
      at.includes("actionrequired")
    ) {
      return {
        actionable: true,
        targetType: "commitment",
        targetId: dbId,
        actionKey,
      };
    }
  }

  return { actionable: false };
};

const resolveActions = (
  notification: any,
  routingType: NotificationRoutingType
): NotificationAction[] => {
  if (notification.actionable !== "yes") {
    return [];
  }
  const target = resolveNotificationTarget(notification);
  if (!target.actionable) {
    return [];
  }

  switch (routingType) {
    case "badge-request":
      return ["issue", "deny", "clarify"];
    case "invitation":
    case "offer":
      return ["accept", "reject"];
    case "question":
    case "nudge":
      return ["answer"];
    case "notify":
    case "unknown":
      return [];
    default:
      return [];
  }
};

/** Delivery channel label for UI (WhatsApp / SMS / App / Slack). */
export function resolveNotificationChannelLabel(
  notification: any
): string | null {
  const raw =
    notification?.channel ??
    notification?.messageChannel ??
    notification?.deliveryChannel ??
    notification?.platform ??
    notification?.messengerType ??
    notification?.sentVia ??
    notification?.source;
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  const normalized = String(raw).toLowerCase().trim();
  if (normalized.includes("whatsapp") || normalized === "wa") {
    return "WhatsApp";
  }
  if (normalized.includes("sms") || normalized === "text") {
    return "SMS";
  }
  if (normalized.includes("slack")) {
    return "Slack";
  }
  if (
    normalized.includes("app") ||
    normalized.includes("web") ||
    normalized.includes("portal")
  ) {
    return "App";
  }
  return String(raw).trim();
}

/** Promise statement inside quotes in WhatsApp/SMS notification bodies. */
export function extractPromiseTextFromNotificationMessage(
  message: string
): string {
  const text = String(message ?? "").trim();
  if (!text) return "";

  const singleQuoted = text.match(/'([^'\n]{8,})'/);
  if (singleQuoted?.[1]) {
    return singleQuoted[1].trim();
  }

  const doubleQuoted = text.match(/"([^"\n]{8,})"/);
  if (doubleQuoted?.[1]) {
    return doubleQuoted[1].trim();
  }

  return text;
}

/** Due date/time embedded in notification message text. */
export function extractDueDateFromNotificationMessage(
  message: string
): string | null {
  const text = String(message ?? "");
  const patterns = [
    /respond\s+by\s+(\d{4}-\d{2}-\d{2}(?:\s+\d{1,2}:\d{2})?(?:\s+hrs?)?)/i,
    /due\s+(\d{4}-\d{2}-\d{2}(?:\s+at\s+\d{1,2}:\d{2})?(?:\s+hrs?)?)/i,
    /by\s+(\d{4}-\d{2}-\d{2}(?:\s+at\s+\d{1,2}:\d{2})?)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match?.[1]) continue;
    return match[1]
      .replace(/\s+at\s+/i, " ")
      .replace(/\s+hrs?$/i, "")
      .trim();
  }

  return null;
}

export const transformNotification = (notification: any) => {
  const receiver = notification.receiver || {};
  const receiverName =
    receiver.firstName && receiver.lastName
      ? `${receiver.firstName} ${receiver.lastName}`.trim()
      : receiver.firstName || receiver.name || "Unknown";

  const sender =
    notification.sender ||
    notification.issuer ||
    notification.committer ||
    {};
  const senderName =
    (sender.firstName && sender.lastName
      ? `${sender.firstName} ${sender.lastName}`.trim()
      : sender.firstName || sender.name) || "Unknown";

  const displayName = senderName !== "Unknown" ? senderName : receiverName;
  const direction: "to" | "from" = "from";

  const message = notification.message || "";
  const titleMatch = message.match(/^([^\n]+)/);
  const title = titleMatch
    ? titleMatch[1].substring(0, 60).trim()
    : "Notification";

  const target = resolveNotificationTarget(notification);
  const linkedInvitationId =
    target.actionable && target.targetType === "invitation"
      ? target.targetId
      : null;
  const linkedCommitmentId =
    target.actionable && target.targetType === "commitment"
      ? target.targetId
      : null;

  const routingType = resolveNotificationActionType(
    notification,
    target.actionable ? target.targetType : null
  );

  let badge = "Notification";
  switch (routingType) {
    case "invitation":
      badge = "Invitation";
      break;
    case "offer":
      badge = "Offer";
      break;
    case "badge-request":
      badge = "Badge Request";
      break;
    case "question":
      badge = "Question";
      break;
    case "notify":
      badge = "Update";
      break;
    case "nudge":
      badge = "Nudge";
      break;
    default:
      badge = "Notification";
  }

  const color = badge === "Badge Request" ? "primary" : "orange";
  const actions = resolveActions(notification, routingType);
  const channelLabel = resolveNotificationChannelLabel(notification);
  const promiseText = extractPromiseTextFromNotificationMessage(message);
  const receivedAt =
    notification.createdAt ||
    notification.updatedAt ||
    notification.receivedAt ||
    notification.received_at;
  const dueFromMessage = extractDueDateFromNotificationMessage(message);
  const dueFromApi =
    notification.dueDate ||
    notification.due_date ||
    notification.commitmentDueDate ||
    notification.commitment?.dueDate;

  return {
    _id: notification._id,
    id: notification._id
      ? parseInt(notification._id.slice(-8), 16) || Math.random() * 1000000
      : Math.random() * 1000000,
    title,
    receivedDate: formatDateTimeCompact(receivedAt, "No date"),
    dueDate: formatDateTimeCompact(
      dueFromMessage || dueFromApi,
      "No due date"
    ),
    promiseText,
    description: message || "No description",
    assignee: displayName,
    type: "notification",
    badge,
    badgeColor: badge === "Badge Request" ? "info" : "warning",
    channelLabel,
    to: displayName,
    actions,
    color,
    direction,
    actionType: routingType,
    linkedInvitationId,
    linkedCommitmentId,
    targetType: target.actionable ? target.targetType : null,
    targetId: target.actionable ? target.targetId : null,
    actionKey: target.actionable ? target.actionKey : null,
    rawNotification: notification,
  };
};
